import React, { useState } from 'react';
import {
  Bell, Mail, MessageSquare, Phone, CheckCircle, Truck,
  DollarSign, Package, Settings, Filter, MoreVertical,
  Clock, AlertCircle
} from 'lucide-react';

// Mock notifications data
const mockNotifications = [
  {
    id: 1,
    type: 'ready',
    title: 'Package Ready for Pickup',
    message: 'Your package BN-45789-JM is ready for pickup at Kingston Hub.',
    channel: 'whatsapp',
    time: '30 minutes ago',
    read: false,
    timestamp: new Date()
  },
  {
    id: 2,
    type: 'payment',
    title: 'Payment Reminder',
    message: 'Invoice INV-8521 (JMD 12,450) is due in 15 days. Pay now to avoid late fees.',
    channel: 'email',
    time: '2 hours ago',
    read: false,
    timestamp: new Date()
  },
  {
    id: 3,
    type: 'transit',
    title: 'Package In Transit',
    message: 'Your package BN-45821-JM has departed Miami and is on its way to Jamaica.',
    channel: 'whatsapp',
    time: '5 hours ago',
    read: true,
    timestamp: new Date()
  },
  {
    id: 4,
    type: 'received',
    title: 'Package Received in Florida',
    message: 'We received your package BN-45835-JM at our Miami warehouse. Weight: 8.5 lbs.',
    channel: 'email',
    time: '1 day ago',
    read: true,
    timestamp: new Date()
  },
  {
    id: 5,
    type: 'payment_confirmed',
    title: 'Payment Confirmed',
    message: 'Your payment of JMD 8,920 for INV-8498 has been received. Thank you!',
    channel: 'email',
    time: '3 days ago',
    read: true,
    timestamp: new Date()
  },
  {
    id: 6,
    type: 'customs',
    title: 'Customs Clearance Complete',
    message: 'Package BN-45789-JM has cleared customs and will be available shortly.',
    channel: 'sms',
    time: '3 days ago',
    read: true,
    timestamp: new Date()
  }
];

const notificationStats = {
  total: 6,
  unread: 2,
  byChannel: {
    whatsapp: 2,
    email: 3,
    sms: 1
  }
};

export default function CustomerNotifications() {
  const [filter, setFilter] = useState('all');
  const [notifications, setNotifications] = useState(mockNotifications);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.read;
    return n.channel === filter;
  });

  const markAsRead = (id) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'ready': return <CheckCircle size={18} />;
      case 'payment': 
      case 'payment_confirmed': return <DollarSign size={18} />;
      case 'transit': return <Truck size={18} />;
      case 'received': return <Package size={18} />;
      case 'customs': return <AlertCircle size={18} />;
      default: return <Bell size={18} />;
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp': return <MessageSquare size={14} />;
      case 'email': return <Mail size={14} />;
      case 'sms': return <Phone size={14} />;
      default: return <Bell size={14} />;
    }
  };

  return (
    <div className="customer-notifications-page" data-testid="customer-notifications">
      {/* Summary Stats */}
      <div className="notification-stats-row">
        <div className="stat-chip all" data-testid="stat-all">
          <Bell size={16} />
          <span>{notificationStats.total} Total</span>
        </div>
        <div className="stat-chip unread" data-testid="stat-unread">
          <Clock size={16} />
          <span>{notificationStats.unread} Unread</span>
        </div>
        <div className="stat-chip whatsapp" data-testid="stat-whatsapp">
          <MessageSquare size={16} />
          <span>{notificationStats.byChannel.whatsapp} WhatsApp</span>
        </div>
        <div className="stat-chip email" data-testid="stat-email">
          <Mail size={16} />
          <span>{notificationStats.byChannel.email} Email</span>
        </div>
        <div className="stat-chip sms" data-testid="stat-sms">
          <Phone size={16} />
          <span>{notificationStats.byChannel.sms} SMS</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="notifications-toolbar">
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >
            All
          </button>
          <button 
            className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
            data-testid="filter-unread"
          >
            Unread
          </button>
          <button 
            className={`filter-tab ${filter === 'whatsapp' ? 'active' : ''}`}
            onClick={() => setFilter('whatsapp')}
            data-testid="filter-whatsapp"
          >
            <MessageSquare size={14} /> WhatsApp
          </button>
          <button 
            className={`filter-tab ${filter === 'email' ? 'active' : ''}`}
            onClick={() => setFilter('email')}
            data-testid="filter-email"
          >
            <Mail size={14} /> Email
          </button>
          <button 
            className={`filter-tab ${filter === 'sms' ? 'active' : ''}`}
            onClick={() => setFilter('sms')}
            data-testid="filter-sms"
          >
            <Phone size={14} /> SMS
          </button>
        </div>
        
        <button 
          className="btn btn-secondary"
          onClick={markAllAsRead}
          disabled={notificationStats.unread === 0}
          data-testid="mark-all-read"
        >
          Mark All as Read
        </button>
      </div>

      {/* Notifications List */}
      <div className="notifications-list">
        {filteredNotifications.length === 0 ? (
          <div className="empty-state">
            <Bell size={48} />
            <h3>No notifications</h3>
            <p>You're all caught up!</p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div 
              key={notification.id}
              className={`notification-card ${notification.read ? 'read' : 'unread'}`}
              onClick={() => markAsRead(notification.id)}
              data-testid={`notification-${notification.id}`}
            >
              <div className={`notification-type-icon ${notification.type}`}>
                {getTypeIcon(notification.type)}
              </div>
              
              <div className="notification-content">
                <div className="notification-header">
                  <h4 className="notification-title">{notification.title}</h4>
                  {!notification.read && <span className="unread-dot" />}
                </div>
                <p className="notification-message">{notification.message}</p>
                <div className="notification-meta">
                  <span className={`channel-badge ${notification.channel}`}>
                    {getChannelIcon(notification.channel)}
                    {notification.channel}
                  </span>
                  <span className="notification-time">
                    <Clock size={12} />
                    {notification.time}
                  </span>
                </div>
              </div>

              <button 
                className="notification-menu-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical size={16} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Notification Preferences Link */}
      <div className="notification-preferences-card">
        <div className="preferences-content">
          <Settings size={20} />
          <div>
            <h4>Notification Preferences</h4>
            <p>Manage how and when you receive notifications</p>
          </div>
        </div>
        <button className="btn btn-secondary" data-testid="manage-preferences">
          Manage Settings
        </button>
      </div>
    </div>
  );
}
